// Fuente única de presentación de estados (label + clases de color) por tipo de documento.
// Reproduce exactamente los mapas que antes estaban duplicados en cada página.

export type StatusDomain = 'budget' | 'remito' | 'invoice' | 'purchase' | 'creditNote' | 'supplyRequest';

type Entry = { label: string; className: string };

const MAPS: Record<StatusDomain, Record<string, Entry>> = {
  budget: {
    pending:   { label: 'Pendiente',  className: 'bg-yellow-100 text-yellow-800' },
    approved:  { label: 'Aprobado',   className: 'bg-green-100 text-green-800' },
    rejected:  { label: 'Rechazado',  className: 'bg-red-100 text-red-800' },
    expired:   { label: 'Expirado',   className: 'bg-gray-100 text-gray-800' },
    completed: { label: 'Completado', className: 'bg-gray-100 text-gray-800' },
    // Versión vieja: la reemplazó una más nueva (P2026-000255 → -v2)
    superseded: { label: 'Reemplazado', className: 'bg-gray-100 text-gray-500' },
  },
  remito: {
    draft:     { label: 'Borrador',  className: 'bg-gray-100 text-gray-800' },
    issued:    { label: 'Emitido',   className: 'bg-blue-100 text-blue-800' },
    delivered: { label: 'Entregado', className: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-800' },
    pending:   { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' },
  },
  invoice: {
    draft:      { label: 'Borrador',   className: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    authorized: { label: 'Autorizada', className: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' },
    sent:       { label: 'Enviada',    className: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-300' },
    paid:       { label: 'Pagada',     className: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300' },
    cancelled:  { label: 'Cancelada',  className: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300' },
  },
  purchase: {
    draft:     { label: 'Borrador',  className: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-200' },
    sent:      { label: 'Enviada',   className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    received:  { label: 'Recibida',  className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    partial:   { label: 'Parcial',   className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  },
  creditNote: {
    authorized: { label: 'Autorizada', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    draft:      { label: 'Borrador',   className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  },
  supplyRequest: {
    pending:   { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
    approved:  { label: 'Aprobado',  className: 'bg-blue-100 text-blue-800' },
    delivered: { label: 'Entregado', className: 'bg-emerald-100 text-emerald-800' },
    rejected:  { label: 'Rechazado', className: 'bg-red-100 text-red-800' },
  },
};

// Clase por defecto por dominio (igual al fallback original de cada pantalla)
const DEFAULT_CLASS: Record<StatusDomain, string> = {
  budget: 'bg-gray-100 text-gray-800',
  remito: 'bg-gray-100 text-gray-800',
  invoice: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  purchase: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-200',
  creditNote: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  supplyRequest: 'bg-gray-100 text-gray-800',
};

export const statusClass = (domain: StatusDomain, code?: string): string =>
  ((code && MAPS[domain][code]?.className) || DEFAULT_CLASS[domain]);

export const statusLabel = (domain: StatusDomain, code?: string): string =>
  ((code && MAPS[domain][code]?.label) || (code || ''));
